// Google Maps Platform data layer for Road Trips.
//
// Google is used as a DATA SOURCE ONLY — the printable map is rendered by our
// own MapLibre pipeline (same as Trail Maps), so the poster stays on-brand.
//   • Places Autocomplete → search a city / landmark / address → a stop.
//   • DirectionsService    → a road-following polyline through the stops.
//
// One browser key (REACT_APP_GOOGLE_MAPS_KEY) powers both. Enable the
// Maps JavaScript API, Places API and Directions API on it, and restrict it by
// HTTP referrer to map-shaped.com (+ localhost for dev). Without a key the page
// still loads; search is disabled with a friendly notice and the route uses a
// straight-line fallback.

export const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
export const hasGoogleKey = !!GOOGLE_KEY;

// Google Directions accepts an origin + destination + up to 23 waypoints.
export const MAX_STOPS = 25;

let loadPromise = null;

/**
 * Inject the Google Maps JS API exactly once (with the Places library) and
 * resolve with `window.google`. Subsequent calls reuse the same promise.
 */
export function loadGoogle() {
    if (!hasGoogleKey) return Promise.reject(new Error('Google Maps key not configured'));
    if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
    if (window.google && window.google.maps) return Promise.resolve(window.google);
    if (loadPromise) return loadPromise;

    loadPromise = new Promise((resolve, reject) => {
        const cbName = '__rt_gmaps_ready';
        window[cbName] = () => {
            delete window[cbName];
            resolve(window.google);
        };
        const s = document.createElement('script');
        s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_KEY)}&libraries=places&callback=${cbName}&loading=async`;
        s.async = true;
        s.defer = true;
        s.onerror = () => {
            loadPromise = null;
            delete window[cbName];
            reject(new Error('Failed to load Google Maps'));
        };
        document.head.appendChild(s);
    });
    return loadPromise;
}

// ── Place search (Places API "New": AutocompleteSuggestion + Place) ─────────
// The legacy `google.maps.places.Autocomplete` widget is deprecated for new
// customers (March 2025) and forces its own Google-styled dropdown onto a bare
// <input>. We instead drive the data API directly and render results in our own
// MUI Autocomplete, so the search field matches the rest of the site chrome.
//
// Billing: a session token groups the keystroke suggestions + the one details
// fetch into a single billed session — create one per search via
// `newSessionToken()`, thread it through `fetchPlaceSuggestions`, and let it
// retire when the user picks a result (resolveSuggestion closes the session).

/** Fresh autocomplete session token (one search = one billed session). */
export async function newSessionToken() {
    await loadGoogle();
    const { AutocompleteSessionToken } = await window.google.maps.importLibrary('places');
    return new AutocompleteSessionToken();
}

/**
 * Type-ahead suggestions for `input`. Returns
 * `[{ placeId, label, primary, secondary, toPlace }]`, where `toPlace()` yields
 * the Place for a follow-up details fetch (keeps the session token's context).
 * Returns `[]` for blank input. Pass the same `sessionToken` for every keystroke
 * of one search.
 */
export async function fetchPlaceSuggestions(input, sessionToken) {
    if (!input || !input.trim()) return [];
    await loadGoogle();
    const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places');
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        ...(sessionToken ? { sessionToken } : {}),
    });
    return (suggestions || [])
        .map((s) => s.placePrediction)
        .filter(Boolean)
        .map((p) => ({
            placeId: p.placeId,
            label: p.text && p.text.text ? p.text.text : '',
            primary: p.mainText && p.mainText.text ? p.mainText.text : '',
            secondary: p.secondaryText && p.secondaryText.text ? p.secondaryText.text : '',
            toPlace: () => p.toPlace(),
        }));
}

/**
 * Resolve a suggestion (from `fetchPlaceSuggestions`) into a stop:
 * `{ name, lat, lng, placeId }`. Fetching the Place's fields closes the
 * billing session that the suggestions belonged to.
 */
export async function resolveSuggestion(opt) {
    await loadGoogle();
    const place = opt.toPlace();
    await place.fetchFields({ fields: ['displayName', 'location', 'formattedAddress'] });
    const loc = place.location;
    return {
        name: place.displayName || place.formattedAddress || opt.label || 'Stop',
        lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
        lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
        placeId: opt.placeId || null,
    };
}

/**
 * Road-following route through the ordered stops via DirectionsService.
 * Resolves `{ coords:[[lng,lat]…], legs:[{distanceM,durationS}], totalDistanceM,
 * totalDurationS, source:'roads' }`. Rejects on any non-OK status so the caller
 * can fall back. Uses the JS SDK (the REST Directions endpoint has no browser
 * CORS).
 */
export async function routeThroughStops(stops) {
    if (!stops || stops.length < 2) throw new Error('Need at least two stops');
    const google = await loadGoogle();
    const svc = new google.maps.DirectionsService();
    const origin = { lat: stops[0].lat, lng: stops[0].lng };
    const destination = { lat: stops[stops.length - 1].lat, lng: stops[stops.length - 1].lng };
    const waypoints = stops.slice(1, -1).map((s) => ({ location: { lat: s.lat, lng: s.lng }, stopover: true }));

    const result = await new Promise((resolve, reject) => {
        svc.route(
            { origin, destination, waypoints, travelMode: google.maps.TravelMode.DRIVING, optimizeWaypoints: false },
            (res, status) => (status === 'OK' && res ? resolve(res) : reject(new Error(`Directions ${status}`)))
        );
    });

    const route = result.routes[0];
    const coords = route.overview_path.map((p) => [p.lng(), p.lat()]);
    // Encoded polyline (compact) for the Static Maps `path=enc:` param — the JS
    // SDK returns it as a string (older builds: an object with `.points`).
    const op = route.overview_polyline;
    const encodedPolyline = typeof op === 'string' ? op : (op && op.points) || null;
    const legs = route.legs.map((l) => ({ distanceM: l.distance ? l.distance.value : null, durationS: l.duration ? l.duration.value : null }));
    const totalDistanceM = legs.reduce((a, l) => a + (l.distanceM || 0), 0);
    const totalDurationS = legs.reduce((a, l) => a + (l.durationS || 0), 0);
    return { coords, encodedPolyline, legs, totalDistanceM, totalDurationS, source: 'roads' };
}

// ── Google Maps Static API (the printable Google basemap) ──────────────────
// The route + numbered markers are baked onto Google's basemap by the Static
// Maps API. Browser <img> requests carry a Referer, so the referrer-restricted
// browser key works for the on-screen PREVIEW. Export/order can't read Google's
// cross-origin pixels into a canvas, so those go through the Cloud Function
// (`fetchStaticMapImage`), which fetches server-side and returns our-origin bytes.

// A clean, low-clutter styled roadmap — visually distinct from the default
// Roadmap (Terrain looked too similar, so it's gone). Style strings use the
// Static Maps `style=` grammar. MIRRORED in functions/index.js (the server
// resolves the maptype id → these styles, so the client can't inject arbitrary
// style params).
export const MINIMAL_STYLES = [
    'feature:poi|visibility:off',
    'feature:transit|visibility:off',
    'feature:landscape|color:0xf5f5f3',
    'feature:water|color:0xcfe8ef',
    'feature:road|element:geometry|color:0xffffff',
    'feature:road|element:labels|visibility:simplified',
    'feature:administrative|element:labels|visibility:simplified',
];

export const STATIC_MAPTYPES = [
    { id: 'roadmap', label: 'Roadmap', gmType: 'roadmap' },
    { id: 'minimal', label: 'Minimal', gmType: 'roadmap', styles: MINIMAL_STYLES },
    { id: 'satellite', label: 'Satellite', gmType: 'satellite' },
    { id: 'hybrid', label: 'Hybrid', gmType: 'hybrid' },
];
const MAPTYPE_BY_ID = Object.fromEntries(STATIC_MAPTYPES.map((m) => [m.id, m]));

/** Build the shared Static Maps query (no key). `maptype` is an id above;
 *  `color` is 0xRRGGBB. */
export function staticMapParams({ polyline, stops = [], maptype = 'roadmap', width = 640, height = 512, scale = 2, color = '0xe8632d' }) {
    const entry = MAPTYPE_BY_ID[maptype] || MAPTYPE_BY_ID.roadmap;
    const p = new URLSearchParams();
    p.set('size', `${Math.round(width)}x${Math.round(height)}`);
    p.set('scale', String(scale));
    p.set('maptype', entry.gmType);
    (entry.styles || []).forEach((s) => p.append('style', s));
    if (polyline) p.append('path', `color:${color}ff|weight:4|enc:${polyline}`);
    else if (stops.length >= 2) p.append('path', `color:${color}ff|weight:4|${stops.map((s) => `${s.lat},${s.lng}`).join('|')}`);
    stops.forEach((s, i) => {
        const label = i < 9 ? `|label:${i + 1}` : ''; // Static markers allow one A–Z/0–9 char
        p.append('markers', `color:${color}${label}|${s.lat},${s.lng}`);
    });
    return p;
}

/** Direct Static Maps URL for the on-screen preview <img> (browser key). */
export function buildStaticMapUrl(opts) {
    const p = staticMapParams(opts);
    p.set('key', GOOGLE_KEY);
    return `https://maps.googleapis.com/maps/api/staticmap?${p.toString()}`;
}

/** Static Maps caps each side at 640; fit the longer one to 640 from the
 *  route's bounding-box aspect. Shared by the render and the product preview. */
export function roadTripDims(stops) {
    const aspect = aspectOf((stops || []).map((s) => [s.lng, s.lat]));
    return aspect >= 1
        ? { width: 640, height: Math.round(640 / aspect) }
        : { width: Math.round(640 * aspect), height: 640 };
}

/** Browser-key preview URL for a road trip — drives BOTH the big preview and
 *  the product-mockup thumbnail, so neither needs the Cloud Function. */
export function roadTripPreviewUrl({ stops, polyline, maptype = 'roadmap', color = '0xe8632d' }) {
    if (!stops || !stops.length) return null;
    const { width, height } = roadTripDims(stops);
    return buildStaticMapUrl({ polyline, stops, maptype, width, height, scale: 2, color });
}

/**
 * Fetch the printable Static Maps PNG via the Cloud Function (server-side, so
 * Google's bytes land on our origin and can be drawn to a canvas / uploaded).
 * Resolves to a same-origin `data:` URL. Requires the `roadTripStaticMap`
 * function deployed with the GOOGLE_MAPS_SERVER_KEY secret.
 */
export async function fetchStaticMapImage(params) {
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('../firebaseApp');
    const call = httpsCallable(functions, 'roadTripStaticMap');
    const res = await call(params);
    return res.data.dataUrl;
}

/**
 * Straight-line fallback when Directions is unavailable (no key, over quota, no
 * drivable route). Connects the stops directly so the map still renders; drive
 * distance/time are unknown.
 */
export function straightLineFallback(stops) {
    return {
        coords: stops.map((s) => [s.lng, s.lat]),
        legs: [],
        totalDistanceM: null,
        totalDurationS: null,
        source: 'straight',
    };
}

/** [[minLng,minLat],[maxLng,maxLat]] over a set of [lng,lat] coordinates. */
export function boundsOf(coords) {
    const lngs = coords.map((c) => c[0]);
    const lats = coords.map((c) => c[1]);
    return [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]];
}

/** Map-area aspect (width / height) from a lng/lat bounds box, clamped so a
 *  mostly east–west or north–south trip still prints on a sensible canvas. */
export function aspectOf(coords, { min = 0.7, max = 1.5 } = {}) {
    if (!coords || coords.length < 2) return 1;
    const [[minLng, minLat], [maxLng, maxLat]] = boundsOf(coords);
    const meanLat = (minLat + maxLat) / 2;
    const wM = (maxLng - minLng) * Math.cos((meanLat * Math.PI) / 180) * 111320;
    const hM = (maxLat - minLat) * 111320;
    if (wM <= 0 || hM <= 0) return 1;
    return Math.max(min, Math.min(max, wM / hM));
}
